import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, TrendingUp, MousePointer, Store, Copy, Check, ExternalLink,
  Globe, Eye, ArrowUpRight, Link2, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const SITE_URL = 'https://theailedger.ai';

const PLATFORMS = [
  { key: 'instagram', label: 'Instagram', color: '#E4405F', icon: 'IG' },
  { key: 'tiktok', label: 'TikTok', color: '#00F2EA', icon: 'TT' },
  { key: 'twitter', label: 'X / Twitter', color: '#1DA1F2', icon: 'X' },
  { key: 'youtube', label: 'YouTube', color: '#FF0000', icon: 'YT' },
  { key: 'facebook', label: 'Facebook', color: '#1877F2', icon: 'FB' },
  { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2', icon: 'LI' },
];

const SOURCE_COLORS = {
  'Instagram': '#E4405F',
  'TikTok': '#00F2EA',
  'X / Twitter': '#1DA1F2',
  'YouTube': '#FF0000',
  'Facebook': '#1877F2',
  'LinkedIn': '#0A66C2',
  'Reddit': '#FF4500',
  'Pinterest': '#E60023',
  'Threads': '#000000',
  'Discord': '#5865F2',
  'Google Search': '#4285F4',
  'Direct': '#00F0FF',
};

export default function LinkAnalyticsTab({ products, creator }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');
  const [customCampaign, setCustomCampaign] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`/creator/link-analytics?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateLink = (product, platform) => {
    const slug = product?.slug || product?.product_id;
    const base = `${SITE_URL}/product/${slug}`;
    const campaign = customCampaign || product?.name?.toLowerCase().replace(/\s+/g, '-') || 'promo';
    return `${base}?utm_source=${platform}&utm_medium=social&utm_campaign=${campaign}`;
  };

  const generateStoreLink = (platform) => {
    if (!creator?.store_slug) return '';
    const base = `${SITE_URL}/store/${creator.store_slug}`;
    return `${base}?utm_source=${platform}&utm_medium=social&utm_campaign=store-promo`;
  };

  const copyLink = (link, key) => {
    try { navigator.clipboard.writeText(link); } catch(err) {
      const t = document.createElement('textarea'); t.value = link;
      document.body.appendChild(t); t.select(); document.execCommand('copy');
      document.body.removeChild(t);
    }
    setCopiedKey(key);
    toast.success('Link copied!');
    setTimeout(() => setCopiedKey(''), 2000);
  };

  const maxTimelineClicks = analytics?.timeline?.length
    ? Math.max(...analytics.timeline.map(t => t.clicks))
    : 1;

  const maxSourceClicks = analytics?.sources?.length
    ? analytics.sources[0].clicks
    : 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1DA1F2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="link-analytics-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Link Analytics
          </h2>
          <p className="text-neutral-400 text-sm mt-1">Track where your traffic is coming from</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              size="sm"
              variant={period === d ? 'default' : 'outline'}
              onClick={() => setPeriod(d)}
              className={period === d
                ? 'bg-[#1DA1F2] text-black rounded-full'
                : 'border-white/10 hover:bg-white/5 text-neutral-300 rounded-full'}
              data-testid={`period-${d}d`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: 'Total Clicks', value: analytics?.total_clicks || 0, icon: MousePointer, color: '#1DA1F2' },
          { label: 'Product Clicks', value: analytics?.product_clicks || 0, icon: Eye, color: '#00F0FF' },
          { label: 'Store Clicks', value: analytics?.store_clicks || 0, icon: Store, color: '#00F0FF' },
          { label: 'Sources', value: analytics?.sources?.length || 0, icon: Globe, color: '#00FF94' },
        ].map(stat => (
          <div key={stat.label} className="bg-neutral-900/50 border border-white/5 rounded-xl p-4" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
              <span className="text-neutral-500 text-xs">{stat.label}</span>
            </div>
            <p className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Traffic Sources */}
      <div className="bg-neutral-900/50 border border-white/5 rounded-xl p-5 md:p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#1DA1F2]" />
          Traffic Sources
        </h3>
        {analytics?.sources?.length ? (
          <div className="space-y-3">
            {analytics.sources.map((s, i) => {
              const pct = maxSourceClicks > 0 ? (s.clicks / maxSourceClicks) * 100 : 0;
              const color = SOURCE_COLORS[s.source] || '#888';
              return (
                <div key={i} data-testid={`source-${s.source.toLowerCase().replace(/[\s\/]/g, '-')}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white font-medium">{s.source}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-neutral-400">
                        {analytics.total_clicks > 0 ? Math.round((s.clicks / analytics.total_clicks) * 100) : 0}%
                      </span>
                      <span className="text-sm font-bold" style={{ color }}>{s.clicks}</span>
                    </div>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Globe className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
            <p className="text-neutral-400 text-sm">No traffic data yet</p>
            <p className="text-neutral-500 text-xs mt-1">Share your links to start tracking</p>
          </div>
        )}
      </div>

      {/* Clicks Timeline */}
      {analytics?.timeline?.length > 0 && (
        <div className="bg-neutral-900/50 border border-white/5 rounded-xl p-5 md:p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
            Clicks Over Time
          </h3>
          <div className="flex items-end gap-1 h-40" data-testid="clicks-timeline-chart">
            {analytics.timeline.slice(-30).map((day, i) => {
              const height = maxTimelineClicks > 0 ? (day.clicks / maxTimelineClicks) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                  <div
                    className="w-full bg-[#1DA1F2]/60 hover:bg-[#1DA1F2] rounded-t transition-colors min-h-[2px]"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${day.date}: ${day.clicks} clicks`}
                  />
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-neutral-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                    {day.date.slice(5)}: {day.clicks}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-neutral-500">
            <span>{analytics.timeline[0]?.date?.slice(5)}</span>
            <span>{analytics.timeline[analytics.timeline.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
      )}

      {/* Top Products */}
      {analytics?.top_products?.length > 0 && (
        <div className="bg-neutral-900/50 border border-white/5 rounded-xl p-5 md:p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <ArrowUpRight className="w-5 h-5 text-white" />
            Top Products by Clicks
          </h3>
          <div className="space-y-2">
            {analytics.top_products.map((p, i) => (
              <div key={p.product_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid={`top-product-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="text-neutral-500 text-xs w-5">{i + 1}.</span>
                  <span className="text-white text-sm font-medium">{p.name}</span>
                </div>
                <span className="text-white font-bold text-sm">{p.clicks}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* UTM Link Generator */}
      <div className="bg-neutral-900/50 border border-white/5 rounded-xl p-5 md:p-6" data-testid="utm-link-generator">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[#00FF94]" />
          Generate Tracking Links
        </h3>
        <p className="text-neutral-400 text-sm mb-5">Create tracked links for each platform to see exactly where your traffic comes from.</p>

        {/* Product / Store selector */}
        <div className="mb-4">
          <label className="text-neutral-400 text-xs mb-2 block">Select what to share</label>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={selectedProduct === null ? 'default' : 'outline'}
              onClick={() => setSelectedProduct(null)}
              className={selectedProduct === null
                ? 'bg-[#00F0FF] text-black rounded-full text-xs'
                : 'border-white/10 text-neutral-300 rounded-full text-xs'}
            >
              <Store className="w-3 h-3 mr-1" /> My Store
            </Button>
            {products?.slice(0, 10).map(p => (
              <Button
                key={p.product_id}
                size="sm"
                variant={selectedProduct?.product_id === p.product_id ? 'default' : 'outline'}
                onClick={() => setSelectedProduct(p)}
                className={selectedProduct?.product_id === p.product_id
                  ? 'bg-[#00F0FF] text-black rounded-full text-xs'
                  : 'border-white/10 text-neutral-300 rounded-full text-xs'}
              >
                {p.name?.length > 20 ? p.name.slice(0, 20) + '...' : p.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Optional campaign name */}
        <div className="mb-5">
          <label className="text-neutral-400 text-xs mb-1.5 block">Campaign name (optional)</label>
          <Input
            value={customCampaign}
            onChange={(e) => setCustomCampaign(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="e.g. spring-launch"
            className="bg-white/5 border-white/10 text-white placeholder:text-neutral-500 h-10 rounded-lg max-w-xs"
            data-testid="campaign-name-input"
          />
        </div>

        {/* Platform links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PLATFORMS.map(platform => {
            const link = selectedProduct
              ? generateLink(selectedProduct, platform.key)
              : generateStoreLink(platform.key);
            const copyKey = `${platform.key}-${selectedProduct?.product_id || 'store'}`;
            return (
              <div key={platform.key} className="flex items-center gap-3 bg-black/30 border border-white/5 rounded-xl p-3" data-testid={`platform-link-${platform.key}`}>
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
                  style={{ backgroundColor: platform.color + '22', color: platform.color }}
                >
                  {platform.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{platform.label}</p>
                  <p className="text-neutral-500 text-[10px] truncate">{link}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyLink(link, copyKey)}
                  className="hover:bg-white/10 rounded-lg px-2 shrink-0"
                  data-testid={`copy-${platform.key}-link`}
                >
                  {copiedKey === copyKey
                    ? <Check className="w-4 h-4 text-green-400" />
                    : <Copy className="w-4 h-4 text-neutral-400" />}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
